import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { Text, Button } from '@comicrelief/component-library';

import SimpleLayout from '@/components/layout/SimpleLayout';
import { paymentErrors } from '@/services/error/paymentErrors';

const isNonProduction = import.meta.env.VITE_CLIENT === 'test';

interface PossibleFetchError {
  data?: {
    message?: string
  }
}

// Common payment error messages that we expect to see.
// If you've come here off the back of a new payment error, please add it to this list,
// IF it's something human readable. Otherwise, add it to the ErrorCustomiser function.
const commonPaymentErrors = [
  'Payment Amount or Currency does not match',
  'Transaction not found',
  'Transaction is already declined',
  'Transaction is already complete',
  'Client not found',
  'Client cannot be empty',
  'Client is not defined',
  'Access Denied',
  'unknown error',
  'required fields are missing',
  'Gateway Rejected: risk_threshold',
  'Gateway Rejected: three_d_secure',
  'Processor Declined',
  'Insufficient Funds',
  'Your card was declined.',
  'Declined - Call Issuer',
  'Your card number is incorrect',
  'Card Issuer Declined CVV',
  'Your card has expired',
  'Gateway Rejected: cvv',
  'Cannot Authorize at this time (Policy)',
  'Cannot Authorize at this time (Life cycle)',
  'Expired Card',
  'Your card has insufficient funds',
  'Your card does not support this type of purchase',
  'Your card\'s security code is incorrect',
  'Closed Card',
  'Authentication Required',
  'Call Issuer. Pick Up Card.',
  'Your card was declined for making repeated attempts too frequently or exceeding its amount limit.',
  'Issuer or Cardholder has put a restriction on the card',
  'No Account',
  'Gateway Rejected: fraud',
  'Do Not Honor',
  'Invalid Account',
  'Your card was declined. You can call your bank for details.',
  'Transaction Not Allowed',
  'Processor Declined - Fraud Suspected',
  'Limit Exceeded',
  'Card Not Activated',
  'Declined',
  'An error occurred while processing your card. Try again in a little bit.'
];

function ErrorFallback({ error, resetError }: { error: unknown; resetError(): void }) {
  const navigate = useNavigate();

  useEffect(() => {
    void navigate('/error', isNonProduction ? {
      state: {
        errorMessage: error instanceof Error ? error.message : 'An unknown error occurred',
        error
      }
    } : undefined);
  }, [navigate, error]);

  const navigateHome = () => {
    resetError();
    void navigate('/');
  };

  if (isNonProduction) {
    console.error('Error caught by ErrorBoundary:', error);
  }

  return (
    <SimpleLayout>
      <Text tag="h1" family="Anton" size="xxl" color="black">Error</Text>

      <p>An error has occurred.</p>

      <Button
        type="submit"
        onClick={navigateHome}
      >
        Return home
      </Button>
    </SimpleLayout>
  );
}

function ErrorContainer({ children }: React.PropsWithChildren) {
  return (
    <Sentry.ErrorBoundary
      fallback={ErrorFallback}
      onError={(error) => {
        // Log to console only in non-production
        if (isNonProduction) {
          console.error('Error caught by Sentry ErrorBoundary:', error);
        }
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

// Checks a passed provider error for a useful, context-specific error msg:
function ErrorCustomiser(errorObject: unknown, fallbackErrorMsg: string) {
  // Ensure we've got a non-null, non-empty string before using it:
  const customMessage = (errorObject as PossibleFetchError)?.data?.message;

  if (customMessage && typeof customMessage === 'string') {
    // Replace gateway errors with user-friendly messages
    switch (customMessage) {
      case 'Gateway Rejected: risk_threshold':
        return paymentErrors.riskThreshold;
      case 'Gateway Rejected: three_d_secure':
        return paymentErrors.threeDSecure;
      default: {
        const isKnownError = commonPaymentErrors.some(error => customMessage.startsWith(error));
        // If it looks like a new payment error, log to Sentry
        if (!isKnownError) {
          Sentry.captureMessage(`New payment rejection reason encountered: ${customMessage}`, 'info');
        }
        return customMessage;
      }
    }
  }

  // Otherwise, just return the fallback:
  return fallbackErrorMsg;
}

export { ErrorContainer, ErrorCustomiser };
